import React, {useCallback, useState} from 'react'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {PIMCard} from '../../Atoms/Cards/PIMCard'
import {
  ShippingAddressErrors,
  ShippingAddressInputContents,
} from '../../Components/InputContentBlocks/AddressInputs/ShippingAddressInputContents'
import {SearchPostCode} from '../../../../Common/SearchPostCode'
import {MBContentBodyDivider} from '../../Atoms/Dividers'
import {MBContentSubTitleTypography} from '../../Atoms/Typographies'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import {useLocation} from 'react-router-dom'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {Spacer} from '../../../../Common/Components/Spacer'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {ShippingAddress} from '../../../DTO/MemberShippingAddressDTO'
import {
  validateShippingAddressInput,
  ValidationShippingAddressInput,
} from '../../../Utils/Validations/ValidationShippingAddressInput'
import {animateScroll} from 'react-scroll'
import {InputErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/InputErrorSummary'
import MemberRepo from '../../../Repo/MemberRepo'
import {useAsync} from 'react-use'
import {MBMemberError} from '../../../Common/MBMemberError'
import {checkAsyncStateAndGetDataOrError, isError} from '../../../Utils/MemberResponseStatusCheck'

type MembersShippingAddressNewInputPageProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
  searchPostCode: SearchPostCode
}

type ErrorState = ShippingAddressErrors & {
  isInputError: boolean
}

export const MembersShippingAddressInputPage: React.VFC<MembersShippingAddressNewInputPageProps>
  = (
  {
    navigator,
    memberRepo,
    searchPostCode,
  },
) => {
  const location = useLocation<ShippingAddress>()
  const locationState = location.state

  const [state, setState] = useState<ShippingAddress>({
    familyName: locationState?.familyName ?? '',
    firstName: locationState?.firstName ?? '',
    familyNameKana: locationState?.familyNameKana ?? '',
    firstNameKana: locationState?.firstNameKana ?? '',
    postCode: locationState?.postCode ?? '',
    addressPrefectureCode: locationState?.addressPrefectureCode ?? '',
    addressCity: locationState?.addressCity ?? '',
    addressStreet: locationState?.addressStreet ?? '',
    addressBuilding: locationState?.addressBuilding ?? '',
    phone: locationState?.phone ?? '',
  })
  const [errorState, setErrorState] = useState<ErrorState>({
    isInputError: false,
    familyNameErrors: [],
    firstNameErrors: [],
    familyNameKanaErrors: [],
    firstNameKanaErrors: [],
    postCodeErrors: [],
    addressPrefectureCodeErrors: [],
    addressCityErrors: [],
    addressStreetErrors: [],
    addressBuildingErrors: [],
    phoneErrors: [],
  })

  const [processing, setProcessing] = useState<boolean>(false)

  const goToShippingAddressInputConfirmPage = useCallback(() => {
    setProcessing(true)

    const inputForValidation: ValidationShippingAddressInput = {
      ...state,
      familyName: state.familyName.trim(),
      firstName: state.firstName.trim(),
      familyNameKana: state.familyNameKana.trim(),
      firstNameKana: state.firstNameKana.trim(),
      postCode: state.postCode.trim(),
      addressPrefectureCode: state.addressPrefectureCode.trim(),
      addressCity: state.addressCity.trim(),
      addressStreet: state.addressStreet.trim(),
      addressBuilding: state.addressBuilding?.trim() ?? '',
      phone: state.phone.trim(),
    }
    setState(prevState => ({
      ...prevState,
      ...inputForValidation,
    }))

    const validationResult = validateShippingAddressInput(inputForValidation)
    if (validationResult.hasError) {
      setErrorState({
        isInputError: true,
        familyNameErrors: validationResult.errors.familyName,
        firstNameErrors: validationResult.errors.firstName,
        familyNameKanaErrors: validationResult.errors.familyNameKana,
        firstNameKanaErrors: validationResult.errors.firstNameKana,
        postCodeErrors: validationResult.errors.postCode,
        addressPrefectureCodeErrors: validationResult.errors.addressPrefectureCode,
        addressCityErrors: validationResult.errors.addressCity,
        addressStreetErrors: validationResult.errors.addressStreet,
        addressBuildingErrors: validationResult.errors.addressBuilding,
        phoneErrors: validationResult.errors.phone,
      })

      setProcessing(false)
      animateScroll.scrollToTop()
      return
    }

    navigator.goToShippingAddressInputConfirmPage(state)
  }, [navigator, state, errorState, processing])


  const goToAccountInformationPage = useCallback((): void => {
    navigator.goToAccountInformationPage()
  }, [navigator])

  const resultGetShippingAddressList = useAsync(async () => {
    return await memberRepo.getShippingAddressList()
  })

  if (!locationState) {
    if (resultGetShippingAddressList.loading) {
      return null
    }

    const shippingAddress = checkAsyncStateAndGetDataOrError(resultGetShippingAddressList)
    if (isError(shippingAddress)) {
      throw shippingAddress
    }
    if (shippingAddress.length > 0) {
      throw new MBMemberError('SystemError')
    }
  }

  return (
    <MembersPageTemplate>
      <PageTitle>商品配送先情報入力</PageTitle>

      <InputErrorSummary display={errorState.isInputError}/>

      <StepGuide activity={'signup'} current={'enter'}/>

      <PIMStack alignItems={'center'}>
        <PIMCard
          headerProps={{title: '商品配送先情報入力'}}
          testId={'block-form'}
        >
          <PIMStack
            divider={<MBContentBodyDivider/>}
            spacing={'18px'}
          >
            <MBContentSubTitleTypography>商品配送先情報を入力してください。</MBContentSubTitleTypography>

            <ShippingAddressInputContents
              activity={'signup'}
              searchPostCode={searchPostCode}
              shippingAddress={state}
              shippingAddressErrors={errorState}
              onChange={(shippingAddress): void => {
                setState((prevState) => {
                  return {
                    ...prevState,
                    ...shippingAddress,
                  }
                })
              }}
            />

          </PIMStack>
        </PIMCard>
      </PIMStack>

      <Spacer mt={'16px'}>
        <MessageForAction>
          上記の内容に間違いがなければ<br/>『登録内容の確認に進む』ボタンを押してください。
        </MessageForAction>
      </Spacer>

      <PIMPageActions>
        <PIMSubmitButton
          testId={'confirm-changes-button'}
          disabled={processing}
          onClick={(): void => {
            goToShippingAddressInputConfirmPage()
          }}
        >
          登録内容の確認に進む
        </PIMSubmitButton>
        <PIMSecondaryButton onClick={goToAccountInformationPage}>
          登録情報に戻る
        </PIMSecondaryButton>
      </PIMPageActions>

    </MembersPageTemplate>
  )
}
